import '../Authorization.css';
import React, { useState } from 'react';
import Login from './Login';
import Verification from './Verification';
import AppHeader from './AppHeader';
import { ALL_FORMS_PATH, MAX_SEND_OTP_ERROR, MAX_VERIFY_OTP_ERROR } from '../consts/consts';
import {
  getAllQuestions,
  getFormsMetadata,
  validateCode,
  verifyPatientAndSendOtp,
} from '../api/AuthorizationApi';
import { InputType } from '../utils/InputType';
import { v4 as uuidv4 } from 'uuid';
import { useLanguage } from '../LanguageContext';
import { getTranslation } from '../consts/translations';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';
import { FormMetadata, Question } from '../types';

interface AuthorizationProps {
  inputPhoneOrEmail: string;
  setInputPhoneOrEmail: (phoneOrEmail: string) => void;
  showVerification: boolean;
  setShowVerification: (show: boolean) => void;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  onExit: () => void;
  setFormsMetadata: (value: any) => void;
  logout: () => void;
  verificationError: string;
  setVerificationError: (error: string) => void;
  setAllQuestions: (questions: any) => void;
}

const Authorization: React.FC<AuthorizationProps> = (props: AuthorizationProps) => {
  const [inputType, setInputType] = useState<InputType>(InputType.Email);
  const [otp, setOtp] = useState('');
  const [validateButtonLoading, setValidateButtonLoading] = useState(false);
  const [sendButtonLoading, setSendButtonLoading] = useState(false);
  const [sendAgainClicked, setSendAgainClicked] = useState(true);
  const { language } = useLanguage();
  const translation = getTranslation(language);
  const navigate = useNavigate();

  const clickExit = () => {
    props.setVerificationError('');
    props.onExit();
  };

  const handleSendVerificationCode = async () => {
    props.setVerificationError('');
    setSendButtonLoading(true);
    try {
      let sessionId = props.sessionId;
      if (!props.sessionId) {
        const newSessionId = uuidv4();
        props.setSessionId(newSessionId);
        sessionId = newSessionId;
      }
      await verifyPatientAndSendOtp(
        props.inputPhoneOrEmail,
        inputType,
        sessionId,
        translation,
        language,
      );
      props.setShowVerification(true);
      setSendAgainClicked(true);
    } catch (error: any) {
      setSendButtonLoading(false);
      if (error.message === MAX_SEND_OTP_ERROR) {
        props.setVerificationError('');
        return;
      }
      props.setVerificationError(error.message);
      setSendAgainClicked(false);
    } finally {
      setSendButtonLoading(false);
    }
  };

  const handleValidateCode = async () => {
    props.setVerificationError('');
    setValidateButtonLoading(true);
    try {
      await validateCode(
        props.inputPhoneOrEmail,
        otp,
        inputType,
        props.sessionId,
        translation,
        language,
      );

      const formsPromise = getFormsMetadata(translation, props.logout);
      const questionsPromise = getAllQuestions(translation, props.logout);

      const formsResponse = await formsPromise;
      props.setFormsMetadata(formsResponse.data as FormMetadata[]);

      navigate(`${ALL_FORMS_PATH}`);

      const questionsResponse = await questionsPromise;
      props.setAllQuestions(questionsResponse.questions as Question[]);
    } catch (error: any) {
      setValidateButtonLoading(true);
      if (error.message === MAX_VERIFY_OTP_ERROR) {
        props.setVerificationError('');
        return;
      }
      props.setVerificationError(error.message);
    } finally {
      setValidateButtonLoading(false);
      setSendAgainClicked(false);
    }
  };

  return (
    <div>
      <div className='authorization-view'>
        <div className='authorization-background-image' />
        <AppHeader
          title={translation.login_title}
          subtitle={translation.login_subtitle}
          height='250px'
        />

        {!props.showVerification ? (
          <Login
            inputPhoneOrEmail={props.inputPhoneOrEmail}
            setInputPhoneOrEmail={props.setInputPhoneOrEmail}
            onSendCode={handleSendVerificationCode}
            loading={sendButtonLoading}
            setShowVerification={props.setShowVerification}
            verificationError={props.verificationError}
            setVerificationError={props.setVerificationError}
            setInputType={setInputType}
          />
        ) : (
          <Verification
            setVerificationCode={setOtp}
            onValidateCode={handleValidateCode}
            buttonDisabled={!!otp}
            loading={validateButtonLoading}
            verificationError={props.verificationError}
            sendVerification={handleSendVerificationCode}
            inputPhoneOrEmail={props.inputPhoneOrEmail}
            onExit={clickExit}
            sendAgainClicked={sendAgainClicked}
            setSendAgainClicked={setSendAgainClicked}
          />
        )}
      </div>
      <Footer showExitButton={props.showVerification} onExit={props.logout} />
    </div>
  );
};

export default Authorization;
